import React, { useEffect, useState } from 'react';
import { ShoppingBag, Menu, X, ChevronDown, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Page } from '../App';
interface NavbarProps {
  onOpenCart: () => void;
  cartCount: number;
  onNavigate: (page: Page) => void;
}
export function Navbar({
  onOpenCart,
  cartCount,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNav = (page: Page) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    setIsCategoriesOpen(false);
  };
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0F0F0F]/95 backdrop-blur-md border-b border-white/10 py-3' : 'bg-transparent py-5'}`} data-id="element-172">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-173">
          <div className="flex items-center justify-between" data-id="element-174">
            {/* Logo */}
            <button onClick={() => handleNav('home')} className="flex items-center gap-2 text-white" data-id="element-175">
              <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-176" />
              <span className="text-xl font-bold font-serif tracking-wider" data-id="element-177">
                LUXE <span className="text-[#D4AF37]" data-id="element-178">COLLECTIONS</span>
              </span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6" data-id="element-179">
              <button onClick={() => handleNav('home')} className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors uppercase tracking-wide" data-id="element-180">
                Home
              </button>

              {/* Categories Dropdown */}
              <div className="relative" data-id="element-181">
                <button onClick={() => setIsCategoriesOpen(!isCategoriesOpen)} className="flex items-center gap-1 text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors uppercase tracking-wide" data-id="element-182">
                  Categories{' '}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isCategoriesOpen ? 'rotate-180' : ''}`} data-id="element-183" />
                </button>
                <AnimatePresence data-id="element-184">
                  {isCategoriesOpen && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 8
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full left-0 mt-2 w-52 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden" data-id="element-185">
                      {[{
                    label: 'Fashion Accessories',
                    page: 'fashion' as Page
                  }, {
                    label: 'Home Appliances',
                    page: 'appliances' as Page
                  }, {
                    label: 'Electronics',
                    page: 'electronics' as Page
                  }].map(cat => <button key={cat.page} onClick={() => handleNav(cat.page)} className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors" data-id="element-186">
                          {cat.label}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <button onClick={() => handleNav('about')} className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors uppercase tracking-wide" data-id="element-187">
                About Us
              </button>

              <button onClick={() => handleNav('track-order')} className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors uppercase tracking-wide" data-id="element-188">
                Track Order
              </button>
            </div>

            {/* Icons */}
            <div className="flex items-center space-x-5" data-id="element-189">
              <button onClick={onOpenCart} className="relative text-gray-300 hover:text-[#D4AF37] transition-colors" data-id="element-190">
                <ShoppingBag className="w-5 h-5" data-id="element-191" />
                {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-192">
                    {cartCount}
                  </span>}
              </button>

              <button onClick={() => handleNav('checkout')} className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-sm font-bold rounded-full hover:bg-[#c4a030] transition-colors" data-id="element-193">
                Checkout
              </button>

              <button className="md:hidden text-gray-300" onClick={() => setIsMobileMenuOpen(true)} data-id="element-194">
                <Menu className="w-6 h-6" data-id="element-195" />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-196">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.3
      }} className="fixed inset-0 z-[60] bg-[#0F0F0F] md:hidden overflow-y-auto" data-id="element-197">
            <div className="p-6 flex flex-col min-h-full" data-id="element-198">
              <div className="flex justify-between items-center mb-10" data-id="element-199">
                <button onClick={() => handleNav('home')} className="flex items-center gap-2" data-id="element-200">
                  <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-201" />
                  <span className="text-xl font-bold font-serif text-white" data-id="element-202">
                    LUXE <span className="text-[#D4AF37]" data-id="element-203">COLLECTIONS</span>
                  </span>
                </button>
                <button onClick={() => setIsMobileMenuOpen(false)} className="text-gray-400 hover:text-white" data-id="element-204">
                  <X className="w-6 h-6" data-id="element-205" />
                </button>
              </div>

              <div className="flex flex-col space-y-2" data-id="element-206">
                {[{
              label: 'Home',
              page: 'home' as Page
            }, {
              label: 'Fashion Accessories',
              page: 'fashion' as Page
            }, {
              label: 'Home Appliances',
              page: 'appliances' as Page
            }, {
              label: 'Electronics',
              page: 'electronics' as Page
            }, {
              label: 'About Us',
              page: 'about' as Page
            }, {
              label: 'Track Order',
              page: 'track-order' as Page
            }, {
              label: 'Checkout',
              page: 'checkout' as Page
            }].map(item => <button key={item.page} onClick={() => handleNav(item.page)} className="text-left text-xl font-serif text-white hover:text-[#D4AF37] transition-colors py-3 border-b border-white/5" data-id="element-207">
                    {item.label}
                  </button>)}
              </div>

              <div className="mt-auto pt-8 border-t border-white/10" data-id="element-208">
                <p className="text-gray-500 text-sm" data-id="element-209">
                  © 2025 Luxe Collections. Benin City, Nigeria.
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}